import React, { FC, PropsWithChildren, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useModal, bTag } from 'core';
import { Tag } from '@brg/api';
import { FlexBox, IPureTagElementsAfter, useToasts, HintTooltip } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';

import {
  AddButton,
  ProjectEditPageGridLabel,
  ContentAddContainer,
  ProjectEditPageGridRow,
  TagsGridNew,
} from 'shared/pages/projectEdit/styles';
import { ProjectAddButton } from 'shared/pages/projectEdit/ProjectAddButton';
import { BTag } from 'core/components';
import { BTagsGridNew } from 'core/components/bTag/bTagsGridNew/bTagsGridNew';
import { isRequired } from 'shared/modals/selectTagsModal/support/isRequired';
import { SelectTagsModalProps } from 'shared/modals/selectTagsModal/types';

import { LabelRedStar } from 'shared/pages/designerEditView/styles';
import { getTooltipStyles } from 'shared/constants/tooptipStyles';
import theme from '../__ADDONS__/__MENU__/src/theme/theme';

type ButtonConfig = {
  selected?: string;
  empty: string;
}

export interface TagFieldProps {
  name: string;
  label?: string;
  modalOptions?: SelectTagsModalProps;
  requiredTags?: Tag[];
  isProjectEdit?: boolean;
  emptyButtonText?: string;
  filledButtonText?: string;
  buttonText?: ButtonConfig;
  required?: boolean;
  minHeight?: number;
  noLabel?: boolean;
}

const MAX_VISIBLE_CHARACTERS = 29;

export const TagsField: FC<PropsWithChildren<TagFieldProps>> = ({
  name,
  label,
  modalOptions = {},
  requiredTags = [],
  buttonText,
  children,
  minHeight,
  required,
  noLabel,
}) => {
  const {
    field: { value: selected = [], onChange },
  } = useController<{ [key: string]: Tag[] }>({ name });
  const modal = useModal();
  const { t, i18n: { language } } = useTranslation();
  const toasts = useToasts();
  const isEducationTag = selected.some((tag: Tag) => tag.isEducationTag);

  const openModal = () => {
    modal.show('tags', {
      value: selected,
      isEdit: true,
      onChange: (tags: Tag[]) => onChange(tags),
      ...modalOptions,
    });
  };

  const removeTag = (tagId: string) => {
    if (modalOptions?.protectedTags?.includes(tagId)) {
      toasts.error('Это обязательный тег');
      return;
    }
    onChange(selected.filter((t) => t.tagId !== tagId));
  };

  const buttonTextValue = useMemo(() => {
    if (buttonText) {
      return selected.length === 0 ? buttonText.empty : buttonText.selected || buttonText.empty;
    }
    return selected.length === 0 ? t('project_edit.tags_button_empty_text') : t('project_edit.tags_button_text');
  }, [selected.length, language, buttonText]);

  const addButtonIsHidden = modalOptions.max && selected.length >= modalOptions.max;

  const icon = useMemo(() => {
    if (modalOptions.max === 1) {
      return IPureTagElementsAfter.change;
    }
    return IPureTagElementsAfter.close;
  }, [modalOptions.max]);

  const handleTagClick = () => {
    openModal();
  };

  const handleIconClick = (tagId: string) => {
    if (modalOptions.max === 1) {
      openModal();
    } else {
      removeTag(tagId);
    }
  };

  const tagList = useMemo(() => {
    return selected.map((tag: Tag) => {
      const title = bTag.title(tag);
      const { tagId } = tag;
      const showTooltip = title?.length > MAX_VISIBLE_CHARACTERS;
      
      return tag.isEducationTag ? (
        <HintTooltip 
          customStyle={getTooltipStyles(theme)} 
          text={t("hints.edit_tags")} 
          z={6} 
          noDisappearDelay 
          positionY="top"
          onWhiteBg
          noArrow
        >
          <BTag
            noTooltip={!showTooltip}
            isShowMoreTag={showTooltip}
            key={tagId}
            tag={tag}
            removable
            educational={isRequired(tag)}
            isProjectsTag
            onClick={handleTagClick}
            onIconClick={icon ? () => handleIconClick(tagId) : undefined}
            icon={icon}
          >
            {title}
          </BTag>
        </HintTooltip>
      ) : (
        <BTag
          noTooltip={!showTooltip}
          isShowMoreTag={showTooltip}
          key={tagId}
          tag={tag}
          removable
          educational={isRequired(tag)}
          isProjectsTag
          onClick={handleTagClick}
          onIconClick={icon ? () => handleIconClick(tagId) : undefined}
          icon={icon}
        >
          {title}
        </BTag>
      );
    });
  }, [selected, icon, t]);

  return (
    <ProjectEditPageGridRow align="flex-start" className={'tags-wrapper mobile-block-6'}>
      {label && !noLabel && (
        <ProjectEditPageGridLabel
          isProjectEditPage
          minHeight={minHeight}
          offsetBottomMobile={16}
        >
          {label}
          {required && <LabelRedStar>*</LabelRedStar>}
        </ProjectEditPageGridLabel>
      )}
      <FlexBox direction={'column'} directionMobile='column' width={'100%'} widthMobile={'100%'} gy={12}>
        {!addButtonIsHidden && (
          <AddButton
            onClick={openModal}
            style={{ width: '100% !important', marginLeft: 0, marginTop: 0, marginBottom: 'auto' }}
            fs
          >
            <ProjectAddButton
              text={buttonTextValue}
              isProjectEdit
              reverse
            />
          </AddButton>
        )}

        {children}

        {selected.length > 0 && (
          <ContentAddContainer fs>
              <TagsGridNew isEducationTag={isEducationTag}>
                {tagList}
                {/* <ReactTooltip id="tooltip-parent-element" /> */}
            </TagsGridNew>
          </ContentAddContainer>
        )}
      </FlexBox>
    </ProjectEditPageGridRow>
  );
};

export default TagsField; import React, { FC, PropsWithChildren, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useModal, bTag } from 'core';
import { Tag } from '@brg/api';
import { FlexBox, IPureTagElementsAfter, useToasts, HintTooltip } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';

import {
  AddButton,
  ProjectEditPageGridLabel,
  ContentAddContainer,
  ProjectEditPageGridRow,
  TagsGridNew,
} from 'shared/pages/projectEdit/styles';
import { ProjectAddButton } from 'shared/pages/projectEdit/ProjectAddButton';
import { BTag } from 'core/components';
import { BTagsGridNew } from 'core/components/bTag/bTagsGridNew/bTagsGridNew';
import { isRequired } from 'shared/modals/selectTagsModal/support/isRequired';
import { SelectTagsModalProps } from 'shared/modals/selectTagsModal/types';

import { LabelRedStar } from 'shared/pages/designerEditView/styles';
import { getTooltipStyles } from 'shared/constants/tooptipStyles';
import theme from '../__ADDONS__/__MENU__/src/theme/theme';

type ButtonConfig = {
  selected?: string;
  empty: string;
}

export interface TagFieldProps {
  name: string;
  label?: string;
  modalOptions?: SelectTagsModalProps;
  requiredTags?: Tag[];
  isProjectEdit?: boolean;
  emptyButtonText?: string;
  filledButtonText?: string;
  buttonText?: ButtonConfig;
  required?: boolean;
  minHeight?: number;
  noLabel?: boolean;
}

const MAX_VISIBLE_CHARACTERS = 29;

export const TagsField: FC<PropsWithChildren<TagFieldProps>> = ({
  name,
  label,
  modalOptions = {},
  requiredTags = [],
  buttonText,
  children,
  minHeight,
  required,
  noLabel,
}) => {
  const {
    field: { value: selected = [], onChange },
  } = useController<{ [key: string]: Tag[] }>({ name });
  const modal = useModal();
  const { t, i18n: { language } } = useTranslation();
  const toasts = useToasts();
  const isEducationTag = selected.some((tag: Tag) => tag.isEducationTag);

  const openModal = () => {
    modal.show('tags', {
      value: selected,
      isEdit: true,
      onChange: (tags: Tag[]) => onChange(tags),
      ...modalOptions,
    });
  };

  const removeTag = (tagId: string) => {
    if (modalOptions?.protectedTags?.includes(tagId)) {
      toasts.error('Это обязательный тег');
      return;
    }
    onChange(selected.filter((t) => t.tagId !== tagId));
  };

  const buttonTextValue = useMemo(() => {
    if (buttonText) {
      return selected.length === 0 ? buttonText.empty : buttonText.selected || buttonText.empty;
    }
    return selected.length === 0 ? t('project_edit.tags_button_empty_text') : t('project_edit.tags_button_text');
  }, [selected.length, language, buttonText]);

  const addButtonIsHidden = modalOptions.max && selected.length >= modalOptions.max;

  const icon = useMemo(() => {
    if (modalOptions.max === 1) {
      return IPureTagElementsAfter.change;
    }
    return IPureTagElementsAfter.close;
  }, [modalOptions.max]);

  const handleTagClick = () => {
    openModal();
  };

  const handleIconClick = (tagId: string) => {
    if (modalOptions.max === 1) {
      openModal();
    } else {
      removeTag(tagId);
    }
  };

  const tagList = useMemo(() => {
    return selected.map((tag: Tag) => {
      const title = bTag.title(tag);
      const { tagId } = tag;
      const showTooltip = title?.length > MAX_VISIBLE_CHARACTERS;
      
      return tag.isEducationTag ? (
        <HintTooltip 
          customStyle={getTooltipStyles(theme)} 
          text={t("hints.edit_tags")} 
          z={6} 
          noDisappearDelay 
          positionY="top"
          onWhiteBg
          noArrow
        >
          <BTag
            noTooltip={!showTooltip}
            isShowMoreTag={showTooltip}
            key={tagId}
            tag={tag}
            removable
            educational={isRequired(tag)}
            isProjectsTag
            onClick={handleTagClick}
            onIconClick={icon ? () => handleIconClick(tagId) : undefined}
            icon={icon}
          >
            {title}
          </BTag>
        </HintTooltip>
      ) : (
        <BTag
          noTooltip={!showTooltip}
          isShowMoreTag={showTooltip}
          key={tagId}
          tag={tag}
          removable
          educational={isRequired(tag)}
          isProjectsTag
          onClick={handleTagClick}
          onIconClick={icon ? () => handleIconClick(tagId) : undefined}
          icon={icon}
        >
          {title}
        </BTag>
      );
    });
  }, [selected, icon, t]);

  return (
    <ProjectEditPageGridRow align="flex-start" className={'tags-wrapper mobile-block-6'}>
      {label && !noLabel && (
        <ProjectEditPageGridLabel
          isProjectEditPage
          minHeight={minHeight}
          offsetBottomMobile={16}
        >
          {label}
          {required && <LabelRedStar>*</LabelRedStar>}
        </ProjectEditPageGridLabel>
      )}
      <FlexBox direction={'column'} directionMobile='column' width={'100%'} widthMobile={'100%'} gy={12}>
        {!addButtonIsHidden && (
          <AddButton
            onClick={openModal}
            style={{ width: '100% !important', marginLeft: 0, marginTop: 0, marginBottom: 'auto' }}
            fs
          >
            <ProjectAddButton
              text={buttonTextValue}
              isProjectEdit
              reverse
            />
          </AddButton>
        )}

        {children}

        {selected.length > 0 && (
          <ContentAddContainer fs>
              <TagsGridNew isEducationTag={isEducationTag}>
                {tagList}
                {/* <ReactTooltip id="tooltip-parent-element" /> */}
            </TagsGridNew>
          </ContentAddContainer>
        )}
      </FlexBox>
    </ProjectEditPageGridRow>
  );
};

export default TagsField; 